import type { CompanyFileDto, CompanyFileMimeGroup } from './types';

export const IMPORT_SOURCE_MIME_GROUPS: CompanyFileMimeGroup[] = ['spreadsheet', 'document', 'pdf'];

export const EVIDENCE_MEDIA_MIME_GROUPS: CompanyFileMimeGroup[] = ['image', 'video'];

export const normalizeMimeGroups = (
  groups?: CompanyFileMimeGroup | CompanyFileMimeGroup[],
): CompanyFileMimeGroup[] => {
  const raw = Array.isArray(groups) ? groups : groups ? [groups] : [];
  const normalized: CompanyFileMimeGroup[] = raw.includes('all') ? ['all'] : raw.filter(Boolean);
  return [...new Set(normalized)];
};

const extensionOf = (name: string): string => {
  const match = name.toLowerCase().match(/\.([a-z0-9]+)$/);
  return match?.[1] ?? '';
};

const fileMime = (file: Pick<CompanyFileDto, 'detected_mime_type' | 'display_name'>): string =>
  (file.detected_mime_type ?? '').toLowerCase();

const SPREADSHEET_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const DOCUMENT_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const SUPPORTED_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'heic', 'webp'];
const EVIDENCE_VIDEO_MIME_BY_EXTENSION: Record<string, string> = {
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
};

export const fileMatchesMimeGroup = (
  file: Pick<CompanyFileDto, 'detected_mime_type' | 'display_name'>,
  group: CompanyFileMimeGroup,
): boolean => {
  if (group === 'all') return true;
  const mime = fileMime(file);
  const ext = extensionOf(file.display_name);

  switch (group) {
    case 'image':
      return mime.startsWith('image/') && SUPPORTED_IMAGE_EXTENSIONS.includes(ext);
    case 'video':
      return EVIDENCE_VIDEO_MIME_BY_EXTENSION[ext] === mime;
    case 'pdf':
      return mime === 'application/pdf' && ext === 'pdf';
    case 'spreadsheet':
      return mime === SPREADSHEET_MIME && ext === 'xlsx';
    case 'document':
      return mime === DOCUMENT_MIME && ext === 'docx';
    case 'other':
      return !(['image', 'video', 'pdf', 'spreadsheet', 'document'] as const).some((known) =>
        fileMatchesMimeGroup(file, known),
      );
  }
};

export const fileMatchesAnyMimeGroup = (
  file: Pick<CompanyFileDto, 'detected_mime_type' | 'display_name'>,
  groups: CompanyFileMimeGroup[],
): boolean =>
  groups.length === 0 ||
  groups.includes('all') ||
  groups.some((group) => fileMatchesMimeGroup(file, group));

export const fileMatchesImportSourceAccept = (
  file: Pick<CompanyFileDto, 'detected_mime_type' | 'display_name'>,
): boolean =>
  fileMatchesMimeGroup(file, 'spreadsheet') ||
  fileMatchesMimeGroup(file, 'document') ||
  fileMatchesMimeGroup(file, 'pdf');

export const fileMatchesEvidenceMediaAccept = (
  file: Pick<CompanyFileDto, 'detected_mime_type' | 'display_name'>,
): boolean => {
  if (fileMatchesMimeGroup(file, 'image')) return true;

  const mime = fileMime(file);
  const ext = extensionOf(file.display_name);
  const acceptedMime = EVIDENCE_VIDEO_MIME_BY_EXTENSION[ext];
  if (!acceptedMime) return false;

  return mime === acceptedMime;
};
